import type { CatalogApp, Environment } from '@/types/catalog';

/**
 * Keycloak `300_entitlements` group paths are `app/<app_path>/<function>`
 * (see keycloak-internal `terraform/300_entitlements/locals.tf`).
 *
 * - Tools (single client): `app/<name>/…` — e.g. `app/gitlab/access`
 * - Env-separated apps: `app/<name>-<ENV>/…` — e.g. `app/grafana-PRODUCTION/admin-access`
 */
export const envEntitlementPrefix = (name: string, environment: Environment): string => `app/${name}-${environment.toUpperCase()}/`;

const stemOf = (prefix: string): string => (prefix.endsWith('/') ? prefix.slice(0, -1) : prefix);

const fold = (value: string): string => value.toLowerCase();

/** Case-insensitive `app/docusign` vs `app/DocuSign/access`. */
const matchesStem = (group: string, stem: string): boolean => {
  const g = fold(group);
  const s = fold(stem);
  return g === s || g.startsWith(`${s}/`);
};

/** `app/<name>/…` — does not treat `app/<name>-<ENV>/…` as a match. */
const matchesExactPrefix = (groups: string[], prefix: string): boolean => {
  const stem = stemOf(prefix);
  return groups.some((group) => matchesStem(group, stem));
};

/** Admin console tiles: only `app/<name>/admin-access`, not `access` or the parent path. */
const matchesAdminAccess = (groups: string[], prefix: string): boolean => {
  const role = `${stemOf(prefix)}/admin-access`;
  return groups.some((group) => matchesStem(group, role));
};

/** `.admin` workforce accounts are `local.admin@domain` (e.g. richard.harper.admin@onetrading.com). */
export const isAdminAccountEmail = (email: string): boolean => /^[^\s@]+\.admin@[^\s@]+$/i.test(email.trim());

/**
 * Env-group suffixes used by `300_entitlements` and catalog tiles.
 * Keycloak uses `PROD`; Grafana embeds `PRODUCTION`; QuestDB embeds `prod`.
 * Longest first so `PRODUCTION` is not stripped as `PROD`.
 */
const envGroupSuffixes = (environment: Environment): string[] => (environment === 'production' ? ['PRODUCTION', 'PROD', 'prod'] : [environment.toUpperCase(), environment]);

const familyStemOf = (stem: string, environment: Environment): string => {
  for (const suffix of envGroupSuffixes(environment)) {
    const tail = `-${suffix}`;
    if (stem.endsWith(tail)) {
      return stem.slice(0, -tail.length);
    }
  }
  return stem;
};

/**
 * Show the tile iff the user has any group under `entitlementPrefix`
 * (keycloak-internal `99_workforce_dashboard` App catalog).
 * `groups` must be full paths with leading `/` already stripped.
 *
 * Tools (`app/<name>/`): any `app/<name>/<function>` or `app/<name>-<ENV>/<function>`.
 * Environment tiles: the env group (`app/<name>-DEV/…`) or the unscoped parent.
 * Keycloak admin consoles (`requiresAdminAccess`): `.admin@` email and `…/admin-access`.
 */
export const isEntitled = (app: CatalogApp, groups: string[], email = ''): boolean => {
  if (app.alwaysVisible) {
    return true;
  }
  if (app.requiresAdminAccess && !isAdminAccountEmail(email)) {
    return false;
  }
  const prefix = app.entitlementPrefix;
  if (!prefix) {
    return false;
  }
  const stem = stemOf(prefix);
  const match = app.requiresAdminAccess ? matchesAdminAccess : matchesExactPrefix;
  if (app.environment) {
    const suffixes = envGroupSuffixes(app.environment);
    const alreadyScoped = suffixes.some((suffix) => stem.endsWith(`-${suffix}`));
    const family = alreadyScoped ? familyStemOf(stem, app.environment) : stem;
    return match(groups, prefix) || suffixes.some((suffix) => match(groups, `${family}-${suffix}/`));
  }
  if (app.requiresAdminAccess) {
    return matchesAdminAccess(groups, prefix);
  }
  return groups.some((group) => {
    const g = fold(group);
    const s = fold(stem);
    return g === s || g.startsWith(`${s}/`) || g.startsWith(`${s}-`);
  });
};

export const filterByEntitlements = (catalog: CatalogApp[], groups: string[], email = ''): CatalogApp[] => catalog.filter((app) => isEntitled(app, groups, email));
